<template>
  <ion-page>
    <ion-content color="primary" class="login-wrapper">
      <div class="d-flex flex-column justify-space-evenly h-100 align-center">
        <div class="login-wrapper__logo ion-justify-content-center">
          <ion-img v-if="arriboLogo" :src="arriboLogo" alt="Arribo Logo"></ion-img>
        </div>
        <div></div>

        <div class="content">
          <ion-card class="ion-padding border-default no-shadow bg-white">
            <h3 class="h3 text-color-body">{{ t('login.title') }}</h3>
            <div class="text-error" data-test="text-error" v-if="showErr">
              {{ t('login.invalidMessage') }}
            </div>
            <div class="mt-lg">
              <form @submit="onSubmit">
                <ion-row gap="0">
                  <ion-col size="12">
                    <ion-input
                      type="email"
                      name="email"
                      v-model="email"
                      v-bind="emailAttrs"
                      class="custom-field"
                      :label="t('login.labels.email')"
                      :placeholder="t('login.labels.email')"
                      label-placement="stacked"
                      fill="outline"
                      mode="md"
                      shape="round"
                    >
                      <ion-icon
                        slot="start"
                        :icon="mailOutline"
                        inner="true"
                        color="primary"
                      ></ion-icon
                    ></ion-input>
                    <ion-text color="error">{{ errors.email }}</ion-text>
                  </ion-col>
                </ion-row>

                <ion-row class="ion-margin-top">
                  <ion-col size="12">
                    <ion-input
                      type="password"
                      name="password"
                      v-model="password"
                      v-bind="passwordAttrs"
                      class="custom-field"
                      :label="t('login.labels.password')"
                      :placeholder="t('login.labels.password')"
                      label-placement="stacked"
                      fill="outline"
                      mode="md"
                      shape="round"
                    >
                      <ion-icon
                        slot="start"
                        :icon="lockClosedOutline"
                        inner="true"
                        color="primary"
                      ></ion-icon>
                      <ion-input-password-toggle slot="end"></ion-input-password-toggle
                    ></ion-input>
                    <ion-text color="error">{{ errors.password }}</ion-text>
                  </ion-col>
                </ion-row>
                <ion-button
                  data-test="goPasswordRecovery"
                  fill="clear"
                  class="text-sm text-underline mt-lg mb-lg"
                  color="primary"
                  expand="block"
                  size="small"
                  @click="goPasswordRecovery"
                >
                  {{ t('login.passwordRecovery') }}
                </ion-button>
                <ion-row class="mt-lg">
                  <ion-col size="12">
                    <ion-button
                      data-test="submit"
                      :disabled="loading"
                      fill="solid"
                      class="text-sm"
                      color="primary"
                      expand="block"
                      size="default"
                      type="submit"
                    >
                      {{ t('login.enterNow') }}
                    </ion-button>
                  </ion-col>
                  <ion-col size="12">
                    <ion-button
                      fill="outline"
                      class="text-sm"
                      color="primary"
                      expand="block"
                      size="small"
                      type="button"
                      @click="goTo('signUp')"
                    >
                      {{ t('login.notAccount') }}
                    </ion-button>
                  </ion-col>
                </ion-row>
              </form>
            </div>
            <ion-progress-bar v-if="loading" type="indeterminate"></ion-progress-bar>
          </ion-card>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>
<script setup lang="ts">
  import {
    IonPage,
    IonContent,
    IonRow,
    IonCol,
    IonImg,
    IonButton,
    IonCard,
    IonProgressBar,
    IonInput,
    IonIcon,
    IonText,
    IonInputPasswordToggle,
  } from '@ionic/vue';
  import arriboLogo from '@/assets/images/arribo-logo.svg';
  import { useAuth } from '@/composables/useAuth';
  import { useI18n } from 'vue-i18n';
  import { ref } from 'vue';
  import { useForm } from 'vee-validate';
  import { lockClosedOutline, mailOutline } from 'ionicons/icons';
  import { LoginForm } from '@/interfaces/auth/auth-store.interface';
  import * as Yup from 'yup';
  import { validEmail } from '@/utils/forms/schemas';

  const { handleAuthLogin, loading, goTo } = useAuth();
  const { t } = useI18n();

  const showErr = ref(false);

  const { defineField, handleSubmit, errors } = useForm({
    validationSchema: {
      email: Yup.string()
        .email(t('rules.email'))
        .matches(validEmail, t('rules.email'))
        .required(t('rules.required')),
      password: Yup.string().required(t('rules.required')),
    },
  });

  const [email, emailAttrs] = defineField('email');
  const [password, passwordAttrs] = defineField('password');

  const onSubmit = handleSubmit((values) => {
    goLogin(values as LoginForm);
  });

  const goLogin = (form: LoginForm) => {
    loading.value = true;
    showErr.value = false;
    try {
      handleAuthLogin(form).then((res) => {
        loading.value = false;
        if (res && res.success) {
          if (res.data?.user.userType === 'relocated') {
            goTo('Guides');
          } else {
            goTo('Explore');
          }
        }
      });
    } catch (error: unknown) {
      loading.value = false;
      const errorMsg = error?.toString() ?? 'Unknown error!';
      if (errorMsg === 'Error: Invalid email or password!') {
        showErr.value = true;
      }
    }
  };

  const goPasswordRecovery = () => {
    goTo('recoveryPassword');
  };
</script>
<style scoped lang="scss">
  .login-wrapper {
    &__logo {
      width: 100%;
      display: flex;
      justify-content: center;

      ion-img {
        width: 190px;
      }
    }

    &__hero {
      width: 100%;
      display: flex;
      justify-content: center;
      max-height: calc(500px - 10%);

      ion-img {
        width: 85%;
      }
    }

    &__claim {
      bottom: 5%;
      width: 100%;
      display: flex;
      justify-content: center;
    }
  }
</style>
